import { useSyncExternalStore } from 'react'

/**
 * The operating system's Reduce Motion preference, live.
 *
 * Read once at mount this would be quietly wrong: people turn Reduce Motion on
 * in the middle of a session, usually because something on screen has just made
 * them feel unwell, and the animation they are reacting to is the one that has
 * to stop. On iOS this is the same switch that governs the whole system, so it
 * gets flipped from Control Center without ever leaving the app.
 *
 * It is a subscription to something outside React, so it is expressed as one.
 * That also closes the gap where the preference changes between the first render
 * and the effect that would have started listening.
 *
 * This reports the OS preference only. The in-app override lives in the settings
 * store, and whatever composes the two owns that decision.
 */

const QUERY = '(prefers-reduced-motion: reduce)'

function currentQuery(): MediaQueryList | null {
  // jsdom has no matchMedia, and neither does a server render, so absence has to
  // mean "no preference expressed" rather than a crash.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  try {
    return window.matchMedia(QUERY)
  } catch {
    return null
  }
}

function subscribe(onChange: () => void): () => void {
  const list = currentQuery()
  if (!list) return () => {}

  if (typeof list.addEventListener === 'function') {
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }

  // Safari 13 and older only have the deprecated pair, and those are exactly the
  // phones most likely to need this preference honoured.
  list.addListener(onChange)
  return () => list.removeListener(onChange)
}

function getSnapshot(): boolean {
  return currentQuery()?.matches ?? false
}

/** No media queries exist on a server, so nothing is reduced there. */
function getServerSnapshot(): boolean {
  return false
}

export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
